import { useCallback, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/constants';
import { authApi } from '../api/authApi';
import { mfaApi } from '../api/mfaApi';
import { removeCmsQueries, useAuthStore } from '../store/authStore';
import type {
	AuthTokens,
	LoginCredentials,
	MfaRequiredResult,
	VerifyMfaInput,
} from '../types';

/**
 * Orchestrates the two-step login: credentials, then (when the backend asks
 * for it) an MFA challenge. The session is only established once the backend
 * returns `status === "authenticated"` with tokens.
 */
export function useLoginFlow() {
	const navigate = useNavigate();
	const setSession = useAuthStore((state) => state.setSession);
	const clearSession = useAuthStore((state) => state.clearSession);
	const [challenge, setChallenge] = useState<MfaRequiredResult | null>(null);

	const establishSession = useCallback(
		async (tokens: AuthTokens) => {
			// Signing in from /login does not always follow a sign-out (another
			// account may still be signed in): never reuse its cached data.
			removeCmsQueries();
			// Store tokens first so the profile request is authenticated.
			setSession({
				user: null,
				accessToken: tokens.accessToken,
				refreshToken: tokens.refreshToken,
			});

			try {
				const user = await authApi.fetchProfile();
				setSession({
					user,
					accessToken: tokens.accessToken,
					refreshToken: tokens.refreshToken,
				});
			} catch (error) {
				clearSession();
				throw error;
			}

			navigate(ROUTES.home, { replace: true });
		},
		[clearSession, navigate, setSession],
	);

	const credentialsMutation = useMutation({
		mutationFn: async (credentials: LoginCredentials) => {
			const result = await authApi.login(credentials);

			if (result.status === 'mfa_required') {
				return { status: 'mfa_required' as const, challenge: result };
			}

			await establishSession(result);
			return { status: 'authenticated' as const };
		},
		onSuccess: (outcome) => {
			if (outcome.status === 'mfa_required') setChallenge(outcome.challenge);
		},
	});

	const mfaMutation = useMutation({
		mutationFn: async (input: VerifyMfaInput) => {
			const result = await mfaApi.verifyMfa(input);

			if (result.status !== 'authenticated') {
				// A `proof` result only occurs during reauthentication, never login.
				throw new Error('Verification did not complete. Please sign in again.');
			}

			await establishSession(result);
		},
	});

	const resetMfa = mfaMutation.reset;
	const resetChallenge = useCallback(() => {
		setChallenge(null);
		resetMfa();
	}, [resetMfa]);

	return {
		challenge,
		submitCredentials: credentialsMutation.mutate,
		submitMfa: mfaMutation.mutate,
		isSubmittingCredentials: credentialsMutation.isPending,
		isSubmittingMfa: mfaMutation.isPending,
		credentialsError: credentialsMutation.error,
		mfaError: mfaMutation.error,
		resetChallenge,
	};
}
